<template lang="pug">
  .task-module-container
    CommonLoadBox(:loaded="loaded")
      template(slot='skeleton')
        TaskSkeleton
      template(slot='module')
        .task-module-container(v-if="taskList && taskList.length > 0")
          .header
            .title {{ moduleConfig.mainTitle }}
            //- 图标 + 副标题（富文本），subtitleLogoSwitch 控制图标渲染
            .max-reward
              img.coin-icon(
                v-if="moduleConfig.subtitleLogoSwitch && moduleConfig.subtitleLogo"
                :src="moduleConfig.subtitleLogo"
              )
              HighlightText.reward-text(
                v-if="moduleConfig.subtitle"
                :text="moduleConfig.subtitle"
                :normalStyle="subtitleNormalStyle"
                :highlightStyle="subtitleHighlightStyle"
              )
          //- 任务列表
          TaskList(
            :double-information="doubleInformation"
            :data="taskList"
            :preparing="false"
            moduleKey="platform_newTask"
            @bounty="$emit('bounty')"
            @refreshTasks="refreshTasks"
          )

    //- 任务弹窗
    TaskPopup(
      :visible="taskPopupVisible"
      :taskInfo="taskPopupInfo"
      @close="handleTaskPopupClose"
      @btnClick="handleTaskPopupBtnClick"
    )
</template>

<script>
import { env } from '@hb/NBridge/lib/h5';
import { HighlightText } from '@hb/platformui-h5';
import { debounce } from '@/utils/index';
import { getVersion } from '@/utils/systemCode';
import eventBus from '@/const/eventBus.js';
import { safeJsonStringify } from '@/utils/tools';
import CommonLoadBox from '@/components/common-load-box';
import { SUBTITLE_NORMAL_STYLE, SUBTITLE_HIGHLIGHT_STYLE } from '../../consts';
import { getTaskPopupData, getTaskListData } from '../../common/task';
import { taskGroupListAdapter } from '../../common/helper';
import TaskList from './task-list';
import TaskSkeleton from '../cpns-skeleton/task-skeleton';
import TaskPopup from '../../new-version-cpns/task-popup';

export default {
  name: 'TaskModule',
  components: {
    TaskList,
    CommonLoadBox,
    TaskSkeleton,
    TaskPopup,
    HighlightText,
  },
  props: {
    // 是否触发展示任务弹窗
    showTaskPopup: {
      type: Boolean,
      default: false,
    },
    moduleConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      // 任务列表
      taskList: [],
      // 翻倍信息
      doubleInformation: {},
      // 记录弹窗任务列表第一个任务
      firstTaskCode: '',
      // 是否加载完成
      loaded: false,
      // 任务弹窗列表信息
      taskPopupInfoList: [],
      // 任务弹窗是否显示
      taskPopupVisible: false,
      // 任务弹窗信息
      taskPopupInfo: {},
      // 可配置副标题文本样式（统一常量）
      subtitleNormalStyle: SUBTITLE_NORMAL_STYLE,
      subtitleHighlightStyle: SUBTITLE_HIGHLIGHT_STYLE,
    };
  },
  computed: {
    // 是否应该触发展示任务弹窗
    shouldTriggerShowTaskPopup() {
      return this.showTaskPopup && this.taskPopupInfoList.length > 0;
    },
  },
  watch: {
    shouldTriggerShowTaskPopup(shouldTrigger) {
      if (shouldTrigger) {
        this.handleTaskPopupData();
      }
    },
  },
  created() {
    // 初始化获取任务列表
    this.getTasks();
  },
  async mounted() {
    // 监听刷新任务列表事件
    eventBus.$on('refreshTaskList', () => {
      this.refreshTasks();
    });
  },
  destroyed() {
    eventBus.$off('refreshTaskList');
  },
  methods: {
    // 刷新任务列表
    refreshTasks(options = {}) {
      this.getTasks({
        isRefresh: true,
        ...options,
      });
    },
    // 获取翻倍广告信息
    getDoubleInformation(items) {
      return {
        bannerAdvertisementUnitId: items.bannerAdvertisementUnitId || '',
        bannerTaskRelationCouponInfo: items.bannerTaskRelationCouponInfo || {},
      };
    },
    // 获取任务列表
    getTasks: debounce(
      async function foo(options) {
        const { isRefresh, degradeTaskCode } = options || {};
        const { success, data } = await getTaskListData({
          newTaskScene: true,
          newWelfare: true,
          firstTaskCode: this.firstTaskCode,
          degradeTaskCode,
        });
        this.loaded = true;
        // 如果获取任务列表失败，则不进行后续逻辑
        if (!success) return;
        const { taskGroupInfoDTOS = [], taskPopupInfoDTOS = [] } = data || {};
        // 处理任务弹窗数据
        this.handleTaskPopupInfo(taskPopupInfoDTOS);

        let list = taskGroupInfoDTOS.filter((item) => item.moduleKey === 'welfare_surprise_tasks_v2');
        // 单单返新版本会返回单单返任务，用户手动做任务时要把单单返类型的任务过滤掉
        list = list.filter((item) => item.taskActivityType !== 10);

        // 任务列表数据处理
        const taskModuleList = await taskGroupListAdapter(
          { list, isRefresh },
          () => {
            this.refreshTasks();
          },
        );

        // 直接取第一个分组的任务列表
        if (taskModuleList && taskModuleList.length > 0) {
          const firstGroup = taskModuleList[0] || {};
          const couponList = firstGroup.taskCouponList || [];
          // 吃饭睡觉补贴任务排序由服务端控制
          this.taskList = couponList;
          this.doubleInformation = {
            bannerAdvertisementUnitId: firstGroup.bannerAdvertisementUnitId || '',
            bannerTaskRelationCouponInfo: firstGroup.bannerTaskRelationCouponInfo || {},
          };
        } else {
          this.taskList = [];
          this.doubleInformation = {};
        }

        // 预加载奖励视频
        if (env.isEasyBikeApp) {
          this.preloadRewardVideo(taskGroupInfoDTOS);
        }
      },
      500,
      true,
    ),
    // 处理任务弹窗数据
    handleTaskPopupInfo(data) {
      // 将任务弹窗数据发送出去
      if (data && data.length !== 0) {
        const { taskCode } = data[0] || {};
        this.firstTaskCode = taskCode;
        this.taskPopupInfoList = data;

        // 更新弹窗状态
        if (this.taskPopupVisible) {
          this.handleTaskPopupData({ popupIsShow: true });
        }
      }
    },
    // 处理任务弹窗数据
    handleTaskPopupData(options) {
      const { popupIsShow } = options || {};
      const { visible, taskInfo } = getTaskPopupData(this.taskPopupInfoList);

      if (!popupIsShow) {
        this.taskPopupVisible = visible;
      }
      if (taskInfo) {
        this.taskPopupInfo = taskInfo;
      }
    },
    // 任务弹窗关闭
    handleTaskPopupClose() {
      this.taskPopupVisible = false;
      this.refreshTasks();
    },
    // 任务弹窗按钮点击
    handleTaskPopupBtnClick({ closePopup, info }) {
      this.taskPopupVisible = !closePopup;
      if (info) {
        this.taskPopupInfo = info;
      }
    },
    // 预加载奖励视频
    async preloadRewardVideo(data) {
      if (data.length === 0) return;

      try {
        const version = await getVersion();
        // 解构成老数据结构
        const oldData = data.flatMap(
          (item) => item.taskCouponList.map((task) => ({
            mainTitle: item.mainTitle, // 保留外层标题
            ...task, // 展开任务详情
          })),
        );
        if (version >= '6.63.0') {
          const videoList = oldData.filter((x) => x.taskActivityType === 8);
          if (videoList.length > 0) {
            if (videoList[0].advertCode) {
              const json = JSON.stringify({
                classMap: 'hellobike/marketing/rewardVideo',
                method: 'preloadRewardVideo',
                params: JSON.stringify({
                  adUnitId: videoList[0].advertCode, // 资源位id
                }),
                callbackId: 'preloadRewardVideoCallbackId',
              });
              if (env.isAndroid) {
                window.AppFunctions.callNative(json);
              } else if (env.isIOS) {
                window.webkit.messageHandlers.callNative.postMessage(json);
              }
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('preloadRewardVideo => error', safeJsonStringify(error));
      }
    },
  },
};
</script>

<style lang="less" scoped>
.task-module-container {
  background-color: #fff;
  border-radius: 0.12rem;

  .header {
    padding: 0.16rem 0.12rem 0.06rem;
    display: flex;
    align-items: center;
    max-width: 2.57rem;

    .title {
      font-size: 0.15rem;
      font-weight: 600;
      color: #242729;
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .max-reward {
      display: flex;
      align-items: center;
      margin-left: 0.08rem;
      flex: 0 1 auto;
      min-width: 0;

      .coin-icon {
        flex: none;
        margin-right: 0.02rem;
        width: 0.16rem;
        height: 0.16rem;
      }

      .reward-text {
        // 颜色仅用于省略号（…），文本本身的颜色由 HighlightText 的内联样式控制
        color: #5a6066;
        font-size: 0.12rem;
        line-height: normal;
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}
</style>
